import { VideoSport } from '@statsbomb/video-client-js';
import { MatchId } from '@statsbomb/video-client-js/dist/src/types/reference';
import { useParachuteVideoAvailability } from '@/hooks/useParachuteVideoAvailability';
import { EventDescription, VisEvent } from '@/types/event';
import { getVideoIdsFromVisEventOrDescriptions } from '@/utils/video';

/**
 * This hook takes a list of VisEvents or EventDescriptions and determines for each event whether video is available or not.
 * First of all it filters based on the video status field of the events.
 * It then uses a hook exposed by the video client to check whether the video is available for the game and period of each event.
 * Once it has determined which videos are available, it returns the video IDs of the remaining available videos, the count of unavailable
 * videos, and an isLoading boolean to indicate whether the hook has finished checking the availability of the videos.
 *
 * NOTE - this hook also returns a ref. It's important that this ref is assigned to a component that is visible on the page.
 * If the ref is ignored, or given to a component that is not visible, the video client will not perform the check and the
 * isLoading state will permanently remain true.
 * */
export const useVideoDataForEvents = <T extends Element>(allEvents: (VisEvent | EventDescription)[]) => {
  const videoAccessibleEvents = allEvents.filter(e => e.videoStatus === 'enabled');
  const distinctGameAndPeriods = Object.entries(
    videoAccessibleEvents.reduce<Record<number, number[]>>((acc, { gameId, period }) => {
      acc[gameId] = [...(acc[gameId] || []), period];
      return acc;
    }, {}),
  );

  const videoReferences = distinctGameAndPeriods.flatMap(([gameId, periods]) =>
    // de-dedupe the periods
    [...new Set(periods)].map(period => ({
      matchId: Number(gameId),
      period,
      sport: VideoSport.Soccer,
    })),
  );

  const [ref, { availability, isReady }] = useParachuteVideoAvailability<T>(videoReferences);

  if (videoReferences.length === 0) {
    return {
      videoIds: [],
      unavailableVideosCount: allEvents.length - videoAccessibleEvents.length,
      ref,
      isLoading: false,
    };
  }

  // If results are not ready, don't return the count of unavailable videos as it will be inaccurate.
  if (!isReady) return { videoIds: [], unavailableVideosCount: 0, ref, isLoading: true };

  const availableVideoGamePeriods = availability.reduce<Record<MatchId, number[]>>(
    (acc, { matchId, period, isAvailable }) => {
      if (isAvailable) {
        acc[matchId] = [...(acc[matchId] || []), period];
      }
      return acc;
    },
    {},
  );

  const availableVideoEvents = videoAccessibleEvents.filter(
    ({ gameId, period }) => availableVideoGamePeriods[gameId]?.includes(period),
  );

  const videoIds = getVideoIdsFromVisEventOrDescriptions(availableVideoEvents);
  const unavailableVideosCount = videoAccessibleEvents.length - videoIds.length;

  return { videoIds, unavailableVideosCount, ref, isLoading: false };
};
